import * as Yup from "yup";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { Formik, FormikProps } from "formik";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import theme from "styles/theme";
import { UserInfo } from "features/meus-dados";

import { Container, FieldContainer, FormContainer } from "./styles";
import {
  ContainerButtons,
  Messenger,
  ModalHeader,
  ModalStyle,
  Title,
} from "../styles";

type EditRegisterEmailProps = {
  openModal: boolean;
  modalFunction: Dispatch<SetStateAction<boolean>>;
  userInfo: UserInfo;
  setEmailAwait: Dispatch<SetStateAction<string>>;
  setOpenEmailConfirmModal: Dispatch<SetStateAction<boolean>>;
  reloadUserInfo?: () => void;
};

type FormValues = {
  cadastro: string;
  email: string;
  emailConfirm: string;
};

const EditRegisterEmail = ({
  openModal,
  modalFunction,
  userInfo,
  setEmailAwait,
  setOpenEmailConfirmModal,
  reloadUserInfo,
}: EditRegisterEmailProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>();

  const initialValues: FormValues = {
    cadastro: userInfo?.email ?? "",
    email: userInfo?.email ?? "",
    emailConfirm: "",
  };

  const validationSchema = (): any =>
    Yup.object().shape({
      email: Yup.string()
        .email("E-mail inválido")
        .required("É necessário digitar um email"),
      emailConfirm: Yup.string().when("cadastro", {
        is: (val) => !val,
        then: Yup.string()
          .email("E-mail inválido")
          .oneOf([Yup.ref("email")], "Os e-mails não combinam")
          .required("É necessário confirmar o e-mail"),
        otherwise: Yup.string(),
      }),
    });

  const requestPatchCustomers = useCallback(
    async (email) => {
      try {
        setLoading(true);
        const headers = {
          access_token: localStorage.getItem(
            "@UltragazEmpresarial:accessToken"
          ),
          EMAIL_CONFIRMATION_URL: `${process.env.REACT_APP_URL}/verificar-email`,
        };
        const { unitId } = JSON.parse(
          localStorage.getItem("@UltragazEmpresarial:unitData")
        );
        const body = {
          email,
          unitId,
        };
        await api.patch(`industrial/customer/v1/customers/me`, body, {
          headers,
        });
        setLoading(false);
        setEmailAwait(email);
        setOpenEmailConfirmModal(true);
        modalFunction(false);
        reloadUserInfo();
      } catch (err) {
        setLoading(false);
        const erro = err.response;
        if (erro) {
          const { message } = err.response.data;
          if (err.response.status !== 401) {
            toast.error(message);
          }
        } else if (err.response.status !== 401) {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      }
    },
    [api, userInfo, setLoading, setEmailAwait, setOpenEmailConfirmModal]
  );

  const handleSubmit = useCallback(
    ({ email }) => {
      if (!loading) {
        if (email !== userInfo.email) {
          requestPatchCustomers(email);
        }
      }
    },
    [requestPatchCustomers, loading]
  );

  return (
    <ModalStyle
      show={openModal}
      animation={false}
      centered
      onHide={() => modalFunction(false)}
    >
      <ModalHeader>
        <span>{userInfo && userInfo.email ? "Editar dados" : "Atenção"}</span>
        <MdClose size={24} onClick={() => modalFunction(false)} />
      </ModalHeader>
      <Container>
        <Title>{userInfo?.email ? "E-mail" : "Cadastre seu e-mail"}</Title>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          render={({
            handleChange,
            handleBlur,
            values,
            errors,
            touched,
          }: FormikProps<FormValues>) => (
            <FormContainer>
              <FieldContainer>
                <TextField
                  label="E-mail *"
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorCondition={errors.email && touched.email}
                  errorMessage={errors.email}
                />
                <Messenger>Será enviado um e-mail para validação</Messenger>
              </FieldContainer>
              {!userInfo ||
                (!userInfo.email && (
                  <TextField
                    label="Confirmar e-mail *"
                    name="emailConfirm"
                    type="email"
                    value={values.emailConfirm}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    errorCondition={errors.emailConfirm && touched.emailConfirm}
                    errorMessage={errors.emailConfirm}
                  />
                ))}
              <ContainerButtons>
                <OutlinedButton
                  color={theme.colors.purple}
                  onClick={() => modalFunction(false)}
                >
                  Cancelar
                </OutlinedButton>
                <ContainedButton type="submit" loading={loading}>
                  Atualizar
                </ContainedButton>
              </ContainerButtons>
            </FormContainer>
          )}
        />
      </Container>
    </ModalStyle>
  );
};

EditRegisterEmail.defaultProps = {
  reloadUserInfo: null,
};

export default EditRegisterEmail;
